import {useReducer, useMemo, useCallback} from 'react';

/**
 * `useDispatchMethods` is a wrapper over `useReducer` to make writing reducer functions easier.
 *
 * Instead of dispatching actions, and handling them in a handrolled reducer funciton, you pass
 * `useDispatchMethods` an object of methods that take the current state and a payload, and return
 * the new state, and it returns an object of methods that, when called, dispatch actions that do
 * the things you just defined.
 *
 * @example
 * const dispatchMethods = {
 *   increment: ({ state }) => state + 1,
 *   decrement: ({ state }) => state - 1,
 *   setValue: ({ payload }) => payload
 * };
 *
 * const initialState = 0;
 *
 * const Example = () => {
 *   const [state, dispatch] = useDispatchMethods(dispatchMethods, initialState);
 *
 *   return (
 *     <div>
 *       <p>The count is {state}</p>
 *       <button onClick={dispatch.increment}>Increment</button>
 *       <button onClick={dispatch.decrement}>Decrement</button>
 *       <input
 *         type='number'
 *         value={state}
 *         onChange={e => {
 *           const num = parseInt(e.target.value, 10)
 *           dispatch.setValue(num)
 *         }}
 *       />
 *     </div>
 *   )
 * }
 *
 * @param {object<string, Function<object>>} methods Object of methods that update state.
 * @param {?*} initialState Value of initial state.
 * @param {?Function} init Function to use for lazy initialization.
 * @param {?Array<*>} dependencyArray Array to use for underlying useCallback and useMemo dependency arrays.
 * @returns {Array<*>} [state, builtDispatch]
 */
export default function useDispatchMethods(methods, initialState, init, dependencyArray = []) {
  const reducer = useCallback((state, {type, payload}) => {
    return methods[type]({state, payload});
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, dependencyArray);

  const [state, dispatch] = useReducer(reducer, initialState, init);

  const builtDispatch = useMemo(
    () =>
      Object.keys(methods).reduce((acc, type) => {
        acc[type] = (payload) => {
          dispatch({
            type,
            payload
          });
        };

        return acc;
      }, {}),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    dependencyArray
  );

  return [state, builtDispatch];
}

export function makeSetStateProperty(property) {
  return ({state, payload}) => ({
    ...state,
    [property]: payload
  });
}

export function makeToggleStateProperty(property) {
  return ({state, payload}) => ({
    ...state,
    [property]: !payload
  });
}
